import * as Sentry from '@sentry/react';
import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useConfig } from '../config';
import { useExamCard } from '../exam-card';
import { useExamCardExpired } from '../exam-card-expired';
import { useExamSession } from '../exam-session';
import { useNetwork } from '../network';
import { useServerTime } from '../server-time';

interface HeartbeatProviderProps {
  children: React.ReactNode;
  enabled?: boolean;
}

interface HeartbeatContextValue {
  isActive: boolean;
  lastSuccessAt: Date | null;
  lastError: string | null;
}

const HeartbeatContext = createContext<HeartbeatContextValue>({
  isActive: false,
  lastSuccessAt: null,
  lastError: null,
});

function HeartbeatProvider({ children, enabled = true }: Readonly<HeartbeatProviderProps>) {
  const { isOnline } = useNetwork();
  const { examSession } = useExamSession();
  const { examCard } = useExamCard();
  const { updateServerTime, registerExternalSync } = useServerTime();
  const [lastSuccessAt, setLastSuccessAt] = useState<Date | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [hasExamCardExpired, setHasExamCardExpired] = useState(false);

  const { heartbeat } = useConfig();
  const { url, intervalMs } = heartbeat ?? {};

  const timerRef = useRef<number | null>(null);
  const { showExamCardExpiredModal } = useExamCardExpired();

  const isActive = useMemo(() => {
    // Start heartbeat only when:
    // - enabled
    // - online
    // - session is present (ExamSessionProvider already skips in break/completed routes)
    // - exam card is not expired
    return Boolean(enabled && isOnline && examSession && !hasExamCardExpired);
  }, [enabled, isOnline, examSession, hasExamCardExpired]);

  const buildRequest = useCallback((): AxiosRequestConfig => {
    const data = {
      examCardId: examCard?.id,
      examPaperId: examSession?.examPaperId,
    };
    return {
      url,
      method: 'post',
      data,
      withCredentials: true,
    };
  }, [url, examCard?.id, examSession?.examPaperId]);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (!isActive) return;

    const unregister = registerExternalSync();

    return () => {
      unregister();
    };
  }, [isActive, registerExternalSync]);

  useEffect(() => {
    if (!isActive || !url) {
      clearTimer();
      return;
    }

    const sendBeat = async () => {
      try {
        const config = buildRequest();
        const response = await axios(config);

        const serverTimestamp = response.data?.currentTime;
        if (serverTimestamp) {
          updateServerTime(serverTimestamp);
        }

        setLastSuccessAt(new Date());
        setLastError(null);
      } catch (err) {
        if (err instanceof AxiosError) {
          const axiosError = err as AxiosError<{ message: string }>;
          if (axiosError.status === 403) {
            setHasExamCardExpired(true);
            showExamCardExpiredModal();
            clearTimer();
            return;
          }
        }

        const msg = err instanceof Error ? err.message : 'Heartbeat failed';
        setLastError(msg);
        Sentry.captureException(err, {
          tags: {
            feature: 'heartbeat',
          },
        });
      }
    };

    sendBeat();

    timerRef.current = window.setInterval(sendBeat, intervalMs);

    return () => {
      clearTimer();
    };
  }, [isActive, intervalMs, buildRequest, url, updateServerTime]);

  const contextValue: HeartbeatContextValue = useMemo(
    () => ({
      isActive,
      lastSuccessAt,
      lastError,
    }),
    [isActive, lastSuccessAt, lastError],
  );

  return <HeartbeatContext.Provider value={contextValue}>{children}</HeartbeatContext.Provider>;
}

function useHeartbeat() {
  const ctx = useContext(HeartbeatContext);
  if (ctx === undefined) {
    throw new Error('useHeartbeat must be used within HeartbeatProvider');
  }
  return ctx;
}

export { HeartbeatProvider, HeartbeatContext, useHeartbeat };
export type { HeartbeatProviderProps, HeartbeatContextValue };
